import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { SHOP_CATEGORIES } from "../../site/data";
import StickyHead from "../StickyHead";

const STATUSES = [
  ["DRAFT", "작성 중"],
  ["ACTIVE", "판매 중"],
  ["SOLDOUT", "품절"],
  ["HIDDEN", "숨김"],
];
const STATUS_LABEL = Object.fromEntries(STATUSES);

const MAX_IMAGES = 10;
const MANUAL_ACCEPT = ".pdf,.zip,.hwp,.hwpx,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.txt,.md";

const EMPTY = {
  name: "", tagline: "", description: "",
  price: "", price_note: "", category: "", status: "DRAFT", stock: "",
  sort_order: 100, resource_url: "", datasheet_url: "",
  specsText: "", featuresText: "", code: "",
};

function formatBytes(n) {
  if (!n) return "";
  if (n < 1024 * 1024) return `${Math.max(1, Math.round(n / 1024))}KB`;
  return `${(n / 1024 / 1024).toFixed(1)}MB`;
}

/* 사양은 "라벨 | 값" 한 줄에 하나로 입력받는다. 표 입력 UI 보다 고치기 빠르다. */
function parseSpecs(text) {
  return (text || "")
    .split("\n")
    .map((line) => line.split("|"))
    .filter((parts) => parts.length >= 2 && parts[0].trim())
    .map((parts) => ({ label: parts[0].trim(), value: parts.slice(1).join("|").trim() }));
}
function specsToText(specs) {
  return (specs || []).map((s) => `${s.label} | ${s.value}`).join("\n");
}
function parseLines(text) {
  return (text || "").split("\n").map((l) => l.trim()).filter(Boolean);
}

function toForm(p) {
  return {
    name: p.name || "", tagline: p.tagline || "", description: p.description || "",
    price: p.price === null || p.price === undefined ? "" : String(p.price),
    price_note: p.price_note || "", category: p.category || "",
    status: p.status || "DRAFT",
    stock: p.stock === null || p.stock === undefined ? "" : String(p.stock),
    sort_order: p.sort_order ?? 100,
    resource_url: p.resource_url || "", datasheet_url: p.datasheet_url || "",
    specsText: specsToText(p.specs), featuresText: (p.features || []).join("\n"),
  };
}

function toPayload(f) {
  return {
    name: f.name.trim(),
    tagline: f.tagline.trim() || null,
    description: f.description || null,
    price: f.price === "" ? null : Number(f.price),
    price_note: f.price_note.trim() || null,
    category: f.category,
    status: f.status,
    stock: f.stock === "" ? null : Number(f.stock),
    sort_order: Number(f.sort_order) || 100,
    resource_url: f.resource_url.trim() || null,
    datasheet_url: f.datasheet_url.trim() || null,
    specs: parseSpecs(f.specsText),
    features: parseLines(f.featuresText),
  };
}

/* 사진 — 저장 전에도 골라 둘 수 있다. 이미 올린 사진은 바로 지우고,
   새로 고른 사진은 '등록/저장' 을 누를 때 올린다. 합쳐서 최대 10장. */
function PhotoPicker({ saved, pending, setPending, busy, onRemoveSaved }) {
  const savedImages = (saved && saved.images) || [];
  const room = MAX_IMAGES - savedImages.length - pending.length;
  const [previews, setPreviews] = useState([]);
  const [warn, setWarn] = useState("");

  useEffect(() => {
    const urls = pending.map((f) => URL.createObjectURL(f));
    setPreviews(urls);
    return () => urls.forEach((u) => URL.revokeObjectURL(u));
  }, [pending]);

  function pick(e) {
    const files = Array.from(e.target.files || []).filter((f) => f.type.startsWith("image/"));
    e.target.value = "";
    if (files.length === 0) return;
    if (files.length > room) {
      setWarn(`사진은 최대 ${MAX_IMAGES}장까지입니다. 앞의 ${Math.max(room, 0)}장만 추가했습니다.`);
    } else {
      setWarn("");
    }
    if (room > 0) setPending((prev) => [...prev, ...files.slice(0, room)]);
  }

  const thumb = { width: 88, height: 88, objectFit: "cover", borderRadius: 8, display: "block" };
  const xBtn = { position: "absolute", top: 4, right: 4, padding: "1px 7px" };
  const total = savedImages.length + pending.length;

  return (
    <div className="sstoa-field" style={{ marginTop: 12 }}>
      <label>
        상품 사진 <span className="sstoa-hint">— {total}/{MAX_IMAGES}장 · 첫 번째가 대표 사진, 판매제품 화면에는 앞의 6장까지 표시</span>
      </label>
      {warn && <div className="sstoa-error" style={{ marginBottom: 8 }}>{warn}</div>}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
        {savedImages.map((key, i) => (
          <div key={key} style={{ position: "relative" }}>
            <img src={`/api/products/image/${key}`} alt="" style={thumb} />
            {i === 0 && <span className="sstoa-tag" style={{ position: "absolute", left: 4, bottom: 4 }}>대표</span>}
            <button
              type="button" onClick={() => onRemoveSaved(key)} disabled={busy}
              className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" style={xBtn} aria-label="사진 삭제"
            >×</button>
          </div>
        ))}
        {pending.map((f, i) => (
          <div key={`${f.name}-${f.size}-${f.lastModified}-${i}`} style={{ position: "relative", opacity: 0.85 }}>
            {previews[i] && <img src={previews[i]} alt="" style={{ ...thumb, outline: "2px dashed var(--s-border, #ccc)" }} />}
            {savedImages.length === 0 && i === 0 && (
              <span className="sstoa-tag" style={{ position: "absolute", left: 4, bottom: 4 }}>대표</span>
            )}
            <button
              type="button" onClick={() => setPending((prev) => prev.filter((_, j) => j !== i))} disabled={busy}
              className="sstoa-btn sstoa-btn--sm" style={xBtn} aria-label="선택 취소"
            >×</button>
          </div>
        ))}
        {total === 0 && <span className="sstoa-hint">아직 사진이 없습니다.</span>}
      </div>
      {room > 0 ? (
        <input type="file" accept="image/*" multiple onChange={pick} disabled={busy} />
      ) : (
        <span className="sstoa-hint">사진이 {MAX_IMAGES}장 가득 찼습니다. 바꾸려면 먼저 지워 주세요.</span>
      )}
      {pending.length > 0 && (
        <span className="sstoa-hint" style={{ marginTop: 6 }}>
          점선 사진 {pending.length}장은 {saved ? "저장" : "등록"}할 때 올라갑니다.
        </span>
      )}
    </div>
  );
}

/* 설명서 — 선택 사항, 하나만 둔다 */
function ManualPicker({ saved, pending, setPending, busy, onRemoveSaved }) {
  const current = saved && saved.manual;
  return (
    <div className="sstoa-field" style={{ marginTop: 12 }}>
      <label>설명서 <span className="sstoa-hint">— 선택 · pdf·zip·hwp·doc·ppt·xls·txt·md, 30MB 이하</span></label>
      {current && !pending && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span>📄 {current.name} <span className="sstoa-hint">{formatBytes(current.size)}</span></span>
          <button type="button" className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={onRemoveSaved} disabled={busy}>
            삭제
          </button>
        </div>
      )}
      {pending && (
        <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 8 }}>
          <span>📄 {pending.name} <span className="sstoa-hint">{formatBytes(pending.size)} · {saved ? "저장" : "등록"}할 때 올라갑니다{current ? " (기존 파일 교체)" : ""}</span></span>
          <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={() => setPending(null)} disabled={busy}>
            취소
          </button>
        </div>
      )}
      <input
        type="file" accept={MANUAL_ACCEPT} disabled={busy}
        onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ""; if (f) setPending(f); }}
      />
    </div>
  );
}

function ProductForm({ initial, onSaved, onCancel }) {
  const [form, setForm] = useState(() => (initial ? toForm(initial) : EMPTY));
  const [saved, setSaved] = useState(initial || null);
  const [photos, setPhotos] = useState([]);   // 아직 올리지 않은 사진 File[]
  const [manual, setManual] = useState(null); // 아직 올리지 않은 설명서 File
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState("");
  const [error, setError] = useState("");
  const upd = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  function apply(p) { setSaved(p); onSaved(p); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name.trim()) { setError("상품명을 입력해 주세요."); return; }
    if (!form.category) { setError("카테고리를 선택해 주세요."); return; }
    setBusy(true); setError("");
    let current = saved;
    try {
      const payload = toPayload(form);
      current = current
        ? await api.post(`/products/${current.id}`, payload)
        : await api.post("/products", { ...payload, code: form.code.trim().toUpperCase() || null });
      apply(current);

      /* 사진은 한 장씩 올린다 — 중간에 실패해도 올라간 사진은 남고, 남은 것만 다시 시도하면 된다 */
      const queue = [...photos];
      while (queue.length > 0) {
        const file = queue[0];
        setProgress(`사진 올리는 중… (${photos.length - queue.length + 1}/${photos.length})`);
        const fd = new FormData();
        fd.append("file", file);
        current = await api.upload(`/products/${current.id}/images`, fd);
        apply(current);
        queue.shift();
        setPhotos([...queue]);
      }

      if (manual) {
        setProgress("설명서 올리는 중…");
        const fd = new FormData();
        fd.append("file", manual);
        current = await api.upload(`/products/${current.id}/manual`, fd);
        apply(current);
        setManual(null);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
      setProgress("");
    }
  }

  async function removeImage(key) {
    if (!window.confirm("이 사진을 삭제할까요?")) return;
    setBusy(true); setError("");
    try { apply(await api.del(`/products/${saved.id}/images?key=${encodeURIComponent(key)}`)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  async function removeManual() {
    if (!window.confirm("설명서를 삭제할까요?")) return;
    setBusy(true); setError("");
    try { apply(await api.del(`/products/${saved.id}/manual`)); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  return (
    <form className="sstoa-panel sstoa-panel--pad" style={{ marginBottom: 12 }} onSubmit={submit}>
      <div className="sstoa-row">
        <div className="sstoa-field">
          <label>카테고리 <span className="req">*</span></label>
          <select className="sstoa-select" value={form.category} onChange={upd("category")} disabled={busy} required>
            <option value="" disabled>선택하세요</option>
            {SHOP_CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
            {form.category && !SHOP_CATEGORIES.includes(form.category) && (
              <option value={form.category} disabled>{form.category} (예전 분류 — 다시 선택해 주세요)</option>
            )}
          </select>
          <span className="sstoa-hint">판매제품 화면의 탭이 됩니다</span>
        </div>
        <div className="sstoa-field">
          <label>상품코드</label>
          {saved ? (
            <input className="sstoa-input" readOnly value={saved.code || ""} />
          ) : (
            <input
              className="sstoa-input" value={form.code} disabled={busy} maxLength={20}
              style={{ fontFamily: "ui-monospace, monospace", textTransform: "uppercase" }}
              onChange={(e) => setForm((f) => ({ ...f, code: e.target.value.toUpperCase().replace(/\s/g, "") }))}
              placeholder="비우면 자동 (예: PCB-0001)"
            />
          )}
          <span className="sstoa-hint">
            {saved ? "등록 후에는 바뀌지 않습니다" : "라오나봇 모듈에 넣은 상품코드가 있으면 그대로 입력 · 영문 대문자·숫자·하이픈"}
          </span>
        </div>
        <div className="sstoa-field" style={{ flex: 2 }}>
          <label>상품명 <span className="req">*</span></label>
          <input className="sstoa-input" value={form.name} onChange={upd("name")} disabled={busy} />
        </div>
      </div>

      <div className="sstoa-field" style={{ marginTop: 12 }}>
        <label>한 줄 소개</label>
        <input className="sstoa-input" value={form.tagline} onChange={upd("tagline")} disabled={busy} />
      </div>

      <div className="sstoa-field" style={{ marginTop: 12 }}>
        <label>상세 설명</label>
        <textarea className="sstoa-textarea" rows={5} value={form.description} onChange={upd("description")} disabled={busy} />
      </div>

      <PhotoPicker
        saved={saved} pending={photos} setPending={setPhotos} busy={busy} onRemoveSaved={removeImage}
      />
      <ManualPicker
        saved={saved} pending={manual} setPending={setManual} busy={busy} onRemoveSaved={removeManual}
      />

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field">
          <label>가격 (원)</label>
          <input className="sstoa-input" type="number" value={form.price} onChange={upd("price")} disabled={busy} placeholder="비우면 '가격 문의'" />
        </div>
        <div className="sstoa-field">
          <label>가격 비고</label>
          <input className="sstoa-input" value={form.price_note} onChange={upd("price_note")} disabled={busy} placeholder="VAT 별도" />
        </div>
        <div className="sstoa-field">
          <label>재고</label>
          <input className="sstoa-input" type="number" value={form.stock} onChange={upd("stock")} disabled={busy} placeholder="비우면 관리 안 함" />
        </div>
      </div>

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field">
          <label>공개 상태</label>
          <select className="sstoa-select" value={form.status} onChange={upd("status")} disabled={busy}>
            {STATUSES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
        </div>
        <div className="sstoa-field">
          <label>정렬 순서</label>
          <input className="sstoa-input" type="number" value={form.sort_order} onChange={upd("sort_order")} disabled={busy} />
        </div>
      </div>

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field">
          <label>데이터시트 · 핀맵 주소</label>
          <input className="sstoa-input" value={form.datasheet_url} onChange={upd("datasheet_url")} disabled={busy} />
        </div>
        <div className="sstoa-field">
          <label>예제 레포 · 템플릿 주소</label>
          <input className="sstoa-input" value={form.resource_url} onChange={upd("resource_url")} disabled={busy} />
        </div>
      </div>

      <div className="sstoa-row" style={{ marginTop: 12 }}>
        <div className="sstoa-field">
          <label>사양표 <span className="sstoa-hint">— 한 줄에 하나, "라벨 | 값"</span></label>
          <textarea
            className="sstoa-textarea" rows={5} value={form.specsText} onChange={upd("specsText")}
            disabled={busy} placeholder={"MCU | STM32F103\n드라이버 | TMC2226 ×3"}
          />
        </div>
        <div className="sstoa-field">
          <label>특징 <span className="sstoa-hint">— 한 줄에 하나</span></label>
          <textarea
            className="sstoa-textarea" rows={5} value={form.featuresText} onChange={upd("featuresText")}
            disabled={busy} placeholder={"3축 동시 제어\nRS485 멀티드롭"}
          />
        </div>
      </div>

      {error && <div className="sstoa-error" style={{ marginTop: 12 }}>{error}</div>}

      <div className="sstoa-actions" style={{ marginTop: 14 }}>
        <button type="submit" className="sstoa-btn sstoa-btn--primary sstoa-btn--sm" disabled={busy}>
          {saved ? "저장" : "등록"}
        </button>
        {saved && (
          <a className="sstoa-btn sstoa-btn--sm" href={`/products/${saved.slug}`} target="_blank" rel="noopener noreferrer">
            사이트에서 보기
          </a>
        )}
        <button type="button" className="sstoa-btn sstoa-btn--sm" onClick={onCancel} disabled={busy}>닫기</button>
        {progress && <span className="sstoa-hint">{progress}</span>}
      </div>
    </form>
  );
}

export default function ProductsAdmin() {
  const [rows, setRows] = useState(null);
  const [editing, setEditing] = useState(null); // null | "new" | product
  const [error, setError] = useState("");
  /* ?edit=상품ID 로 들어오면 그 상품을 바로 연다 (라오나 상품 연결 화면의 [상품 고치기]) */
  const [params, setParams] = useSearchParams();

  async function load() {
    setError("");
    try { setRows(await api.get("/products")); }
    catch (err) { setError(err.message); }
  }
  useEffect(() => { load(); }, []);
  useEffect(() => {
    const id = Number(params.get("edit"));
    if (!rows || !id) return;
    const p = rows.find((r) => r.id === id);
    if (p) setEditing(p);
    setParams({}, { replace: true });
  }, [rows, params, setParams]);

  async function remove(id) {
    if (!window.confirm("이 상품을 삭제할까요? 사진과 설명서도 함께 지워집니다.")) return;
    try { await api.del(`/products/${id}`); setEditing(null); load(); }
    catch (err) { setError(err.message); }
  }

  return (
    <div>
      <StickyHead>
        <div style={{ marginBottom: 12 }}>
          <Link to="/sstoa" className="sstoa-back">← 홈</Link>
        </div>

        <div className="sstoa-topbar">
          <div>
            <h1 className="sstoa-title">판매제품 관리</h1>
            <p className="sstoa-sub">홈페이지 판매제품 페이지에 노출할 상품을 등록합니다. 판매 중 · 품절 상태만 사이트에 보입니다.</p>
          </div>
          {!editing && (
            <button className="sstoa-btn sstoa-btn--primary" onClick={() => setEditing("new")}>+ 상품 등록</button>
          )}
        </div>
      </StickyHead>

      {error && <div className="sstoa-error" style={{ marginBottom: 12 }}>{error}</div>}

      {editing && (
        <ProductForm
          key={editing === "new" ? "new" : editing.id}
          initial={editing === "new" ? null : editing}
          onSaved={() => load()}
          onCancel={() => { setEditing(null); load(); }}
        />
      )}

      <div className="sstoa-panel">
        {rows === null ? (
          <div className="sstoa-empty">불러오는 중…</div>
        ) : rows.length === 0 ? (
          <div className="sstoa-empty">등록된 상품이 없습니다.</div>
        ) : (
          <table className="sstoa-table">
            <thead>
              <tr><th>상태</th><th>상품코드</th><th>상품</th><th>카테고리</th><th>가격</th><th>재고</th><th>조회</th><th></th></tr>
            </thead>
            <tbody>
              {rows.map((p) => (
                <tr key={p.id}>
                  <td>
                    <span className={`sstoa-tag ${p.status === "ACTIVE" ? "" : "sstoa-tag--gray"}`}>
                      {STATUS_LABEL[p.status] || p.status}
                    </span>
                  </td>
                  <td style={{ fontFamily: "ui-monospace, monospace", whiteSpace: "nowrap" }}>{p.code || "—"}</td>
                  <td style={{ fontWeight: 600 }}>
                    {p.name}
                    <div style={{ fontWeight: 400, fontSize: 12.5, color: "var(--s-fg-mute)", marginTop: 2 }}>
                      사진 {(p.images || []).length}장{p.manual ? " · 설명서 있음" : ""}
                    </div>
                  </td>
                  <td>{p.category || "—"}</td>
                  <td>{p.price === null || p.price === undefined ? "문의" : `${p.price.toLocaleString("ko-KR")}원`}</td>
                  <td>{p.stock === null || p.stock === undefined ? "—" : p.stock}</td>
                  <td>{p.view_count || 0}</td>
                  <td style={{ textAlign: "right", whiteSpace: "nowrap" }}>
                    <button className="sstoa-btn sstoa-btn--sm" onClick={() => setEditing(p)}>수정</button>{" "}
                    <button className="sstoa-btn sstoa-btn--danger sstoa-btn--sm" onClick={() => remove(p.id)}>삭제</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
